import {ProtocolCommand} from '../service-model/protocol-command.interface';
import {ProtocolQuery} from '../service-model/protocol-query.interface';
import {SHELL_PROTOCOL_OPERATIONS_NS} from '../shell-communication/operations/shell-protocol-namespace.const';

export type HorizontalSkewBasis = 'spot' | 'forward';

// ── Persisted per-surface preferences (saved atomically as one object) ──
// Legs are DAY COUNTS (owner, 2026-07-02 round 2 — reverted from picked expirations):
// each resolves to the nearest listed expiration at read time. Fresh surfaces start 30/90.
export interface HorizontalSkewPrefs {
    short: number;
    long: number;
    basis: HorizontalSkewBasis;
}

// Where a surface keeps its prefs. The tray persists per-user (a UserSettingsService
// key; the underlying arg is ignored); an OPG panel persists PER SYMBOL
// (`${underlying}.opg.hskew`) — first use of a symbol starts at the 30/90 defaults.
export interface HorizontalSkewPrefsStore {
    load(underlying: string): HorizontalSkewPrefs | null;
    save(underlying: string, prefs: HorizontalSkewPrefs): void;
}

// ── Live push: one entry per expiration the slot's subscribers need ──
export interface HorizontalSkewExpirationIvDto {
    expiration: string;      // yyyy-MM-dd
    iv: number | null;       // ATM IV, UNROUNDED; null while loading
    strike: number | null;   // the ATM strike actually used
    loading: boolean;
}

// Backend leg resolution for one distinct (shortDays, longDays) pair on the slot (D19):
// a surface matches its own day counts here to learn which expirations its legs resolved
// to, as-of the push's asOfDate (QG cursor date in replay, today in live). Null resolved
// expirations = unresolvable as-of that date (settle on N/A, do not spin).
export interface HorizontalSkewLegsDto {
    shortDays: number;
    longDays: number;
    shortExpiration: string | null;
    longExpiration: string | null;
    shortDte: number | null;
    longDte: number | null;
}

// The per-(underlying, basis) term-structure push. The ratio is NOT on the wire —
// each surface divides its own Short/Long legs out of `expirations`, mapped via `legs`.
// ⚠ protobuf-net collapses empty repeated fields to null across the ESS round-trip —
// null-guard both arrays.
export interface HorizontalSkewChangedDto {
    underlying: string;
    basis: HorizontalSkewBasis;
    expirations: HorizontalSkewExpirationIvDto[];
    loading: boolean;
    legs: HorizontalSkewLegsDto[];
    asOfDate: string;        // yyyy-MM-dd the legs were resolved against
}

// ── Subscribe / Unsubscribe a session to one (underlying, basis) + leg DAY COUNTS ──
// The backend resolves the day counts to expirations (D19) — the frontend never resolves.
export class SubscribeHorizontalSkew implements ProtocolCommand {
    constructor(
        public readonly underlying: string,
        public readonly basis: string,
        public readonly shortDays: number,
        public readonly longDays: number,
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.SubscribeHorizontalSkew`;
    }
}

export class UnsubscribeHorizontalSkew implements ProtocolCommand {
    constructor(
        public readonly underlying: string,
        public readonly basis: string,
        public readonly shortDays: number,
        public readonly longDays: number,
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.UnsubscribeHorizontalSkew`;
    }
}

// ── One-shot term-structure curve snapshot for the Plot ──
export interface HorizontalSkewCurvePoint {
    expiration: string;
    iv: number | null;
    strike: number | null;
}

export class GetHorizontalSkewCurve implements ProtocolQuery {
    constructor(
        public readonly underlying: string,
        public readonly basis: string,
        public readonly startExpiration: string,
        public readonly endExpiration: string,
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetHorizontalSkewCurve`;
    }
}

export interface GetHorizontalSkewCurveReply {
    underlying: string;
    basis: string;
    points: HorizontalSkewCurvePoint[];
    // "yyyy-MM-dd" when the underlying is in QG History replay (the curve's DTEs and candidate
    // chain describe THAT date's world — shown in the legend); null/absent in live mode.
    asOfDate?: string | null;
}
